import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MenuBar from "./MenuBar";
import PayrunOverview from "./PayrunOverview";
import CheckList from "@/components/CheckList";
import { useAuth } from "@/hooks/useAuth";
import {
  isNannyUser,
  isSuperUser,
  isContractorUser,
  isClientUser,
} from "@/types/user";
import EmployeePayslips from "@/components/employee/EmployeePayslips";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { SetupChecklistResponse } from "@/types/checklist";
import { DashboardViewResponse } from "@/types/payrun";
import { Menu, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import ContractorDashboard from "@/components/contractor/ContractorDashboard";
import ClientDashboard from "@/components/client/ClientDashboard";
import { ClientInvoicesContainer } from "@/components/client/ClientInvoicesContainer";
import { useEmployees } from "@/contexts/EmployeeContext";
import EmployeeSelector from "@/components/common/EmployeeSelector";
import { Badge } from "@/components/ui/badge";
import BillingAPIService from "@/services/billing-api";
import type { SeatSummary } from "@/types/billing";

const DashboardCard: React.FC<{
  title: string;
  value: string | number;
  subtitle: string;
  link: string;
}> = ({ title, value, subtitle, link }) => (
  <Link to={link} className="block no-underline">
    <Card className="transition-all hover:shadow-md hover:scale-[1.02]">
      <CardHeader>
        <div className="text-sm text-gray-500">{title}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {typeof value === "number" && title.includes("Total")
            ? `£${value.toLocaleString()}`
            : value}
        </div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </CardContent>
    </Card>
  </Link>
);

const ImplementationMessage: React.FC = () => (
  <div className="w-full">
    <h1 className="text-3xl mb-6">Welcome to Hirelo</h1>
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl mb-4">Thanks for signing up for Hirelo!</h2>
        <p className="text-gray-700">
          Please reach out to your Implementation Specialist Bobby Chadha at{" "}
          <a
            href="mailto:bobbyc@hirelo.io"
            className="text-blue-600 hover:text-blue-800"
          >
            bobbyc@hirelo.io
          </a>{" "}
          for next steps.
        </p>
      </CardContent>
    </Card>
  </div>
);

const EmployerDashboard: React.FC<{
  selectedUserId: string;
  isSuperUser: boolean;
}> = ({ selectedUserId, isSuperUser }) => {
  const [checklistData, setChecklistData] =
    useState<SetupChecklistResponse | null>(null);
  const [dashboardData, setDashboardData] =
    useState<DashboardViewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [seatSummary, setSeatSummary] = useState<SeatSummary | null>(null);
  const { user } = useAuth();
  const { employees, includeTerminated, setIncludeTerminated } = useEmployees();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const queryParams: Record<string, string | number> = {};
        if (selectedUserId !== "current") {
          queryParams.userId = selectedUserId;
        }

        const [checklistResponse, dashboardResponse] = await Promise.all([
          ApiService.request<SetupChecklistResponse>(
            EndpointKeys.GET_EMPLOYER_CHECKLIST,
            { queryParams },
          ),
          ApiService.request<DashboardViewResponse>(
            EndpointKeys.GET_DASHBOARD_VIEWS,
            { queryParams },
          ),
        ]);

        setChecklistData(checklistResponse);
        setDashboardData(dashboardResponse);

        // Fetch seats summary
        try {
          const summary = await BillingAPIService.getSeatSummary(
            selectedUserId !== "current" ? Number(selectedUserId) : undefined,
          );
          setSeatSummary(summary);
        } catch (e) {
          console.error("Failed to fetch seat summary for dashboard", e);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedUserId]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isSuperUser && selectedUserId !== "current") {
    return (
      <div className="w-full dashboard-tour">
        <h1 className="text-3xl font-bold mb-6">Payroll Dashboard</h1>

        {/* Employee quick switcher and summary */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <EmployeeSelector label="Quick switch employee" placeholder="All employees" />
            <div className="text-sm text-muted-foreground">
              Active employees: {employees.filter((e) => e.isActive).length} / {employees.length}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <input
              id="show-terminated"
              type="checkbox"
              checked={includeTerminated}
              onChange={(e) => setIncludeTerminated(e.target.checked)}
            />
            <label htmlFor="show-terminated">Include terminated</label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {employees.map((emp) => (
              <Link key={emp.id} to={`/employee/${emp.id}`} className="block no-underline">
                <Card className="hover:shadow-sm transition-all">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{emp.firstName} {emp.lastName}</div>
                      <Badge variant={emp.isActive ? "default" : "secondary"}>
                        {emp.isActive ? "Active" : "Terminated"}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        <div
          id="dashboard-section"
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          <DashboardCard
            title="Total Employer Cost"
            value={parseFloat(dashboardData?.totalEmployerBurden || "0") || 0}
            subtitle="This month"
            link={
              dashboardData?.latestPayrunId
                ? `/payroll-approval/${dashboardData?.latestPayrunId}`
                : "/"
            }
          />
          <DashboardCard
            title="Employees Paid"
            value={dashboardData?.numEmployeesPaid || 0}
            subtitle="Full-time & Part-time"
            link="/employees"
          />
          <DashboardCard
            title="Pending Payrun Approvals"
            value={dashboardData?.submissionsRequiringApproval || 0}
            subtitle="Awaiting review"
            link="/employee-submissions"
          />
          <DashboardCard
            title="Active Employees"
            value={employees.filter((e) => e.isActive).length}
            subtitle="Currently available for payruns"
            link="/employees"
          />
          <DashboardCard
            title="Seats"
            value={`${seatSummary?.active_employees ?? 0} / ${seatSummary?.paid_seats ?? 0}`}
            subtitle={`Available ${seatSummary?.available_seats ?? 0}`}
            link="/employees?manageSeats=1"
          />
        </div>

        <h2 id="payroll-section" className="text-2xl font-semibold mb-4">
          Recent Payruns
        </h2>
        <PayrunOverview selectedUserId={selectedUserId} />
      </div>
    );
  }

  if (!user?.inImplementation && !checklistData?.totalCompleted) {
    return <ImplementationMessage />;
  }

  if (checklistData && !checklistData.canRunPayroll) {
    return (
      <div className="w-full">
        <h1 className="text-3xl font-bold mb-6">Welcome to Hirelo</h1>
        <CheckList
          items={checklistData.items}
          totalCompleted={checklistData.totalCompleted}
          totalItems={checklistData.totalItems}
        />
      </div>
    );
  }

  return (
    <div className="w-full dashboard-tour">
      <h1 className="text-3xl font-bold mb-6">Payroll Dashboard</h1>

      {/* Employee quick switcher and summary */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <EmployeeSelector label="Quick switch employee" placeholder="All employees" />
          <div className="text-sm text-muted-foreground">
            Active employees: {employees.filter((e) => e.isActive).length} / {employees.length}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <input
            id="show-terminated"
            type="checkbox"
            checked={includeTerminated}
            onChange={(e) => setIncludeTerminated(e.target.checked)}
          />
          <label htmlFor="show-terminated">Include terminated</label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {employees.map((emp) => (
            <Link key={emp.id} to={`/employee/${emp.id}`} className="block no-underline">
              <Card className="hover:shadow-sm transition-all">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{emp.firstName} {emp.lastName}</div>
                    <Badge variant={emp.isActive ? "default" : "secondary"}>
                      {emp.isActive ? "Active" : "Terminated"}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      <div
        id="dashboard-section"
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
      >
        <DashboardCard
          title="Total Employer Cost"
          value={parseFloat(dashboardData?.totalEmployerBurden || "0") || 0}
          subtitle="This month"
          link={
            dashboardData?.latestPayrunId
              ? `/payroll-approval/${dashboardData?.latestPayrunId}`
              : "/"
          }
        />
        <DashboardCard
          title="Employees Paid"
          value={dashboardData?.numEmployeesPaid || 0}
          subtitle="Full-time & Part-time"
          link="/employees"
        />
        <DashboardCard
          title="Pending Payrun Approvals"
          value={dashboardData?.submissionsRequiringApproval || 0}
          subtitle="Awaiting review"
          link="/employee-submissions"
        />
        <DashboardCard
          title="Active Employees"
          value={employees.filter((e) => e.isActive).length}
          subtitle="Currently available for payruns"
          link="/employees"
        />
        <DashboardCard
          title="Seats"
          value={`${seatSummary?.active_employees ?? 0} / ${seatSummary?.paid_seats ?? 0}`}
          subtitle={`Available ${seatSummary?.available_seats ?? 0}`}
          link="/employees?manageSeats=1"
        />
      </div>

      <h2 id="payroll-section" className="text-2xl font-semibold mb-4">
        Recent Payruns
      </h2>
      <PayrunOverview selectedUserId={selectedUserId} />
    </div>
  );
};

const NannyDashboard: React.FC = () => {
  return (
    <div className="w-full">
      <h1 className="text-3xl font-bold mb-6">My Dashboard</h1>
      <div className="grid grid-cols-1 gap-4">
        <h2 className="text-2xl font-semibold mt-4">My Recent Payslips</h2>
        <EmployeePayslips isDashboard={true} />
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const isSuperUserAccount = isSuperUser(user);

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  if (isContractorUser(user)) {
    return <ContractorDashboard />;
  }

  if (isClientUser(user)) {
    return <ClientInvoicesContainer />;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        id="settings-section"
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 px-4 md:px-6 py-6 overflow-y-auto bg-gray-100">
        <div className="max-w-7xl">
          {isSuperUserAccount && (
            <div className="mb-6 bg-white p-4 rounded-lg shadow border border-gray-200">
              <h2 className="text-lg font-semibold mb-2">
                Super User Controls
              </h2>
              <p className="text-sm text-gray-500 mb-3">
                Select an employer account to view or manage:
              </p>
              <SuperUserEmployerSelector
                selectedUserId={selectedUserId}
                onUserChange={handleUserChange}
              />
            </div>
          )}

          {user && isNannyUser(user) ? (
            <NannyDashboard />
          ) : (
            <EmployerDashboard
              selectedUserId={selectedUserId}
              isSuperUser={isSuperUserAccount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
